import { t, Trans } from '@lingui/macro';
import React, { FunctionComponent, useEffect } from 'react';

import { useFetch } from '@api/ApiHelper';
import { ApiDatumResponse } from '@api/v4/ApiResponseTypes';
import { ApiKeyAttributes, ApiKeySource } from '@api/v4/organizations/api_keys';
import { DangerButton } from '@components/library/button';
import { DialogSizes, StandardDialog } from '@components/library/dialog';

import classes from '../styles/revoke-api-key-modal.module.scss';

interface RevokeApiKeyModalProps {
  apiKeyId: string;
  keyName: string;
  onRevoked?: () => void;
  open: boolean;
  organizationKey: string;
  setOpen: (open: boolean) => void;
  source: ApiKeySource;
}

type RevokeApiKeyResponse = ApiDatumResponse<ApiKeyAttributes, 'api_key'>;

const buildRevokeUrl = (
  organizationKey: string,
  apiKeyId: string,
  source: ApiKeySource
): string => {
  const baseUrl = `/api/v4/organizations/${organizationKey}/api_keys/${apiKeyId}`;
  return source === 'legacy' ? `${baseUrl}?source=legacy` : baseUrl;
};

export const RevokeApiKeyModal: FunctionComponent<RevokeApiKeyModalProps> = ({
  apiKeyId,
  keyName,
  onRevoked,
  open,
  organizationKey,
  setOpen,
  source,
}) => {
  const revokeApiKeyOptions = {
    fetchOnMount: false,
    method: 'DELETE' as const,
    url: buildRevokeUrl(organizationKey, apiKeyId, source),
  };

  const revokeApiKey = useFetch<RevokeApiKeyResponse>(revokeApiKeyOptions);
  const isRevoking = revokeApiKey.loading;

  useEffect(() => {
    if (!open) {
      revokeApiKey.reset();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!revokeApiKey.response) return;

    Notify.create({
      title: t`API Key ${keyName} revoked successfully`,
      type: 'success',
    });
    onRevoked?.();
    setOpen(false);
  }, [revokeApiKey.response]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!revokeApiKey.error) return;

    Notify.create({
      body: revokeApiKey.error.message || t`Please try again or contact support`,
      title: t`Error revoking API Key`,
      type: 'error',
    });
  }, [revokeApiKey.error]);

  const handleRevoke = (): void => {
    revokeApiKey.fetch({
      ...revokeApiKeyOptions,
      url: buildRevokeUrl(organizationKey, apiKeyId, source),
    });
  };

  // Legacy names already include "'s key" from the server, so omit the extra suffix.
  const title = source === 'legacy' ? t`Revoke ${keyName}` : t`Revoke ${keyName}'s key`;

  return (
    <StandardDialog
      dialogBodyClassName={classes.revokeApiKeyModalBody}
      dialogClassName={classes.revokeApiKeyModal}
      dialogHeaderClassName={classes.revokeApiKeyModalHeader}
      id="revoke-api-key-modal"
      open={open}
      setOpen={setOpen}
      showFooter={false}
      size={DialogSizes.Auto}
      title={title}
      width={520}
    >
      <div className={classes.content}>
        <p className={classes.warningText}>
          <Trans>
            The API key(s) associated with this name will be immediately disabled. API requests made using these key(s)
            will be rejected. Once revoked, you will no longer be able to modify the key(s).
          </Trans>
        </p>

        <div className={classes.actions}>
          <DangerButton
            className={classes.revokeButton}
            disabled={isRevoking}
            isLoading={isRevoking}
            loadingCopy={t`Revoking...`}
            onClick={handleRevoke}
            size="small"
            type="button"
          >
            <Trans>Revoke Key</Trans>
          </DangerButton>
        </div>
      </div>
    </StandardDialog>
  );
};
